import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ImportCSVDialog = ({ onImportComplete }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/domains/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Imported ${response.data.success} domains successfully!`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} domains failed to import`);
      }
      
      setOpen(false);
      setFile(null);
      onImportComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'domain_name,notes\nexample.com,My example domain\ntest.org,Testing domain';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'domain_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="import-csv-button">
          <Upload className="h-4 w-4 mr-2" />
          Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="import-csv-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Import Domains from CSV</DialogTitle>
          <DialogDescription className="font-body">
            Upload a CSV file with domain names to monitor
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="csv-upload"
              disabled={loading}
            />
            <label htmlFor="csv-upload" className="cursor-pointer">
              <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {file ? file.name : 'Click to select CSV file'}
              </p>
            </label>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={downloadTemplate}
            className="w-full"
            data-testid="download-template-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>

          <Button
            onClick={handleUpload}
            disabled={!file || loading}
            className="w-full"
            data-testid="upload-csv-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              'Upload and Import'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};